// Phase 2D.2 checkpoint 2B — the Bulgarian user-management vocabulary and the
// client-side rules of the invite form.
//
// THE BACKEND AND THE DATABASE ARE AUTHORITATIVE. Everything here exists so an
// obvious mistake does not need a round trip and so a number already computed by
// the database can be phrased correctly in Bulgarian. In particular:
//
//   * SEAT USAGE IS NEVER CALCULATED HERE. The invitation list endpoint returns
//     limit / usage / available / active_members / pending_invitations per role,
//     and this module only turns those numbers into grammatical sentences. Usage
//     means active members PLUS pending, non-expired invitations, which is why
//     the total is never called "активни потребители";
//   * RESEND ELIGIBILITY IS NEVER DECIDED HERE. can_resend and the remaining
//     cooldown come from the database, which uses the same clock the resend RPC
//     enforces;
//   * a role a person "may invite" is a hint for the dialog only. The database
//     re-derives the acting role from the membership and applies the allow-list
//     again (owner -> manager|sales, manager -> sales, never owner).

export const ROLE_LABEL = Object.freeze({
  owner: "Собственик",
  manager: "Мениджър",
  sales: "Търговец",
});

// The GENITIVE PLURAL, for "Свободни места за търговци". Owner is absent on
// purpose: a tenant invitation can never target the Owner.
export const ROLE_PLURAL_FOR = Object.freeze({
  manager: "мениджъри",
  sales: "търговци",
});

// The ACCUSATIVE SINGULAR, for the title "Добавяне на търговец" that a dialog with
// exactly ONE invitable role carries (Phase 2D.2 checkpoint 5). Owner is absent
// for the same reason.
export const ROLE_ACCUSATIVE = Object.freeze({
  manager: "мениджър",
  sales: "търговец",
});

export const SEAT_ROLE_TITLE = Object.freeze({
  manager: "Мениджърски места",
  sales: "Места за търговци",
});

export const STATE_LABEL = Object.freeze({
  active: "Активен",
  inactive: "Неактивен",
});

export const INVITATION_STATUS_LABEL = Object.freeze({
  pending: "Чакаща",
  accepted: "Приета",
  revoked: "Отменена",
  expired: "Изтекла",
});

// "Приета от имейл доставчика" is deliberately NOT "доставена": the provider
// accepting a message says nothing about an inbox, a spam folder or a bounce.
export const DELIVERY_STATUS_LABEL = Object.freeze({
  unsent: "Неизпратена",
  delivered_to_provider: "Приета от имейл доставчика",
  delivery_failed: "Грешка при изпращане",
});

// Sanitized codes the backend may store. Anything unknown falls back to the
// generic sentence, so a new code can never render an English internal label and
// a raw provider message never reaches a screen.
const DELIVERY_ERROR_MESSAGE = Object.freeze({
  email_disabled: "Изпращането на имейли е изключено в тази среда.",
  security_sender_not_configured:
    "Изпращачът на защитени имейли не е настроен. Свържете се с OfferTrace.",
  supabase_invite_rejected:
    "Системата за акаунти не прие поканата. Опитайте да я изпратите отново.",
  auth_account_already_established:
    "Този човек вече има активен профил в OfferTrace. Изпратете поканата отново, " +
    "за да получи покана за присъединяване вместо покана за създаване на профил.",
  app_base_url_missing: "Адресът на приложението не е настроен. Свържете се с OfferTrace.",
  app_base_url_invalid: "Адресът на приложението не е валиден. Свържете се с OfferTrace.",
  api_key_missing: "Имейл услугата не е настроена. Свържете се с OfferTrace.",
});

const GENERIC_DELIVERY_ERROR =
  "Имейлът не беше изпратен. Опитайте да изпратите поканата отново.";

export function deliveryErrorMessage(code) {
  if (!code) return "";
  return DELIVERY_ERROR_MESSAGE[code] || GENERIC_DELIVERY_ERROR;
}

export const FILTERS = Object.freeze(["all", "pending", "accepted", "revoked", "expired"]);

export const FILTER_LABEL = Object.freeze({
  all: "Всички",
  pending: "Чакащи",
  accepted: "Приети",
  revoked: "Отменени",
  expired: "Изтекли",
});

export function roleLabel(role) {
  return ROLE_LABEL[role] || "";
}

export function stateLabel(isActive) {
  return isActive ? STATE_LABEL.active : STATE_LABEL.inactive;
}

// ---------------------------------------------------------------------------
// Bulgarian grammar for the seat picture
// ---------------------------------------------------------------------------
function n(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** "място" for one, "места" for anything else. */
export function seatWord(count) {
  return n(count) === 1 ? "място" : "места";
}

/** "1 свободно място" / "3 свободни места" / "0 свободни места". */
export function availableSeatsText(available) {
  const value = n(available);
  return value === 1 ? "1 свободно място" : `${value} свободни места`;
}

/** "1 чакаща покана" / "2 чакащи покани". */
export function pendingInvitationsText(pending) {
  const value = n(pending);
  return value === 1 ? "1 чакаща покана" : `${value} чакащи покани`;
}

/**
 * The approved live line for the invite dialog.
 * "Свободни места за търговци: 1 от 2"
 */
export function availableSeatsLine(role, seat) {
  const plural = ROLE_PLURAL_FOR[role];
  if (!plural || !seat) return "";
  return `Свободни места за ${plural}: ${n(seat.available)} от ${n(seat.limit)}`;
}

/**
 * The reservation sentence, only when a pending invitation actually holds a seat.
 * "1 място е резервирано от чакаща покана." / "2 места са резервирани от чакащи покани."
 */
export function reservedSeatsLine(pending) {
  const value = n(pending);
  if (value <= 0) return "";
  if (value === 1) return "1 място е резервирано от чакаща покана.";
  return `${value} места са резервирани от чакащи покани.`;
}

/** "Заети места: 2 от 3" — usage, which INCLUDES pending invitations. */
export function occupiedSeatsLine(seat) {
  if (!seat) return "";
  return `Заети места: ${n(seat.usage)} от ${n(seat.limit)}`;
}

export const NO_SEATS_MESSAGE = "Няма свободни лицензирани места за тази роля.";

/** A role is offerable when the actor may invite it AND it is licensed at all. */
export function invitableRoles(permissions, seats) {
  const out = [];
  if (permissions?.can_invite_manager && n(seats?.manager?.limit) > 0) out.push("manager");
  if (permissions?.can_invite_sales && n(seats?.sales?.limit) > 0) out.push("sales");
  return out;
}

export function hasFreeSeat(seats, role) {
  return n(seats?.[role]?.available) > 0;
}

// ---------------------------------------------------------------------------
// The invite form
// ---------------------------------------------------------------------------
export const MAX_PERSON_NAME = 100;
export const MAX_EMAIL = 320;
export const MIN_PHONE = 4;
export const MAX_PHONE = 40;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const FORM_MESSAGES = Object.freeze({
  first_name: "Въведете име (до 100 знака).",
  last_name: "Въведете фамилия (до 100 знака).",
  email: "Въведете валиден имейл адрес.",
  phone: "Телефонът трябва да е между 4 и 40 знака.",
  role: "Изберете роля.",
});

export function validateInviteDraft(draft, allowedRoles) {
  const errors = {};
  const first = (draft?.first_name || "").trim();
  const last = (draft?.last_name || "").trim();
  const email = (draft?.email || "").trim();
  const phone = (draft?.phone || "").trim();
  const role = draft?.role || "";

  if (!first || first.length > MAX_PERSON_NAME) errors.first_name = FORM_MESSAGES.first_name;
  if (!last || last.length > MAX_PERSON_NAME) errors.last_name = FORM_MESSAGES.last_name;
  if (!email || email.length > MAX_EMAIL || !EMAIL_RE.test(email)) {
    errors.email = FORM_MESSAGES.email;
  }
  if (phone && (phone.length < MIN_PHONE || phone.length > MAX_PHONE)) {
    errors.phone = FORM_MESSAGES.phone;
  }
  if (!role || !(allowedRoles || []).includes(role)) errors.role = FORM_MESSAGES.role;
  return errors;
}

/** The payload the API expects, normalised so the fingerprint is stable. */
export function invitePayload(draft) {
  const phone = (draft?.phone || "").trim();
  const teamId = (draft?.team_id || "").trim();
  return {
    first_name: (draft?.first_name || "").trim(),
    last_name: (draft?.last_name || "").trim(),
    email: (draft?.email || "").trim(),
    role: draft?.role,
    ...(phone ? { phone } : {}),
    // Phase 2D.3A - the OPTIONAL Sales team. Omitted entirely when nothing was
    // chosen, so the invitation keeps the legacy null-team contract; the server
    // decides what a Manager may promise.
    ...(teamId && draft?.role === "sales" ? { team_id: teamId } : {}),
  };
}

/**
 * ONE idempotency key per user ACTION.
 *
 * The dialog keeps the key while the payload is unchanged, so an automatic retry
 * of the SAME submission is recognised as a replay and sends no second e-mail. A
 * changed payload is a different decision and gets a new key, which is what
 * makes ``idempotency_conflict`` meaningful.
 */
export function newRequestId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (err) {
    // Fall through to the manual generator below.
  }
  let out = "";
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < 32; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/** A stable signature of the submitted values, used to detect a changed form. */
export function payloadSignature(draft) {
  const payload = invitePayload(draft);
  return [
    payload.role || "",
    (payload.email || "").toLowerCase(),
    payload.first_name || "",
    payload.last_name || "",
    payload.phone || "",
    // Phase 2D.3A - the promised team is part of the invitation, so changing it
    // and resubmitting is a NEW request and must never replay the previous one.
    payload.team_id || "",
  ].join("|");
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
/** The Bulgarian sentence the backend attached, or the fallback. */
export function apiMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
}

export function apiCode(err) {
  const detail = err?.response?.data?.detail;
  return detail && typeof detail === "object" && typeof detail.code === "string"
    ? detail.code
    : "";
}

/** Field errors the backend reported, already in Bulgarian. */
export function apiFieldErrors(err) {
  const errors = err?.response?.data?.detail?.errors;
  return errors && typeof errors === "object" ? errors : null;
}

/** How long the server says to wait before resending again. */
export function retryAfterSeconds(err) {
  const value = err?.response?.data?.detail?.context?.retry_after_seconds;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.ceil(parsed) : 0;
}

export function cooldownText(seconds) {
  const value = Math.max(n(seconds), 1);
  return `Можете да изпратите поканата отново след ${value} ${
    value === 1 ? "секунда" : "секунди"
  }.`;
}

// ---------------------------------------------------------------------------
// Phase 2D.2 checkpoint 3 — member lifecycle
//
// Same rule as everything above: NOTHING here decides anything. Whether a member
// may be deactivated, whether a replacement owner is mandatory, whether a seat is
// free and whether a role is licensed are answered by
// GET /organization/members/{member_id}/lifecycle-impact, in the database, and are
// only phrased in Bulgarian here. Every action is addressed by the MEMBERSHIP id
// the roster returns, never by an e-mail address. The two role labels come from
// ROLE_LABEL, so "Собственик" can never appear as a choice.
// ---------------------------------------------------------------------------

/** The two selling roles a lifecycle action may target. Owner is absent. */
export const LIFECYCLE_ROLES = Object.freeze(["manager", "sales"]);

/** Only an Owner acts, and only on a Manager or a Sales member — never on itself. */
export function canManageMember(actorRole, member) {
  if (actorRole !== "owner" || !member) return false;
  if (member.is_self) return false;
  if (!member.member_id) return false;
  // Phase 2D.3A - the server marks a row that may never be a lifecycle target
  // (today the Owner row, which a Manager now also sees). Honour the FLAG instead
  // of inferring the rule from a role label.
  if (member.is_protected === true) return false;
  return LIFECYCLE_ROLES.indexOf(member.role) !== -1;
}

/** "1 активна оферта" / "4 активни оферти". */
export function openQuotationsText(count) {
  const value = n(count);
  return value === 1 ? "1 активна оферта" : `${value} активни оферти`;
}

/** The approved sentence that names the consequence before the Owner confirms. */
export function deactivationSummary(impact) {
  const open = n(impact?.open_quotations);
  if (open > 0) {
    return `Потребителят има ${openQuotationsText(open)}. За да го деактивирате, първо изберете нов отговорник.`;
  }
  return (
    "Потребителят губи достъп до организацията, лицензираното място се освобождава, " +
    "а историята и досегашните оферти остават непроменени."
  );
}

export const REACTIVATION_SUMMARY =
  "Потребителят получава отново достъп до организацията и заема едно лицензирано място в текущата си роля.";

export const NO_SEAT_FOR_ROLE = "Няма свободно лицензирано място за тази роля.";

export const ROLE_NOT_LICENSED =
  "Тази роля не е включена в лиценза на организацията. Свържете се с OfferTrace, за да я активирате.";

/** "Ролята ще бъде променена от Търговец на Мениджър." */
export function roleChangeSentence(fromRole, toRole) {
  const from = ROLE_LABEL[fromRole] || "";
  const to = ROLE_LABEL[toRole] || "";
  if (!from || !to) return "";
  return `Ролята ще бъде променена от ${from} на ${to}.`;
}

/** Why a role change cannot happen right now, or an empty string. */
export function roleChangeBlocker(roleChange) {
  if (!roleChange) return "";
  if (roleChange.licensed === false) return ROLE_NOT_LICENSED;
  if (roleChange.allowed === false) return NO_SEAT_FOR_ROLE;
  return "";
}

/**
 * Who may receive the open quotations: the ACTIVE members of this organization,
 * except the person being deactivated. The roster is the same authoritative list
 * the page already displays, the value of every option is the MEMBERSHIP id, and
 * the database validates the choice again inside the deactivation transaction.
 */
export function replacementCandidates(members, targetMemberId) {
  return (Array.isArray(members) ? members : [])
    .filter((m) => m && m.is_active && m.member_id && m.member_id !== targetMemberId)
    .map((m) => ({
      member_id: m.member_id,
      label: `${[m.first_name, m.last_name].filter(Boolean).join(" ") || m.full_name || m.email} — ${
        m.role_label || roleLabel(m.role)
      }`,
    }));
}

// ---------------------------------------------------------------------------
// Phase 2D.2 checkpoint 4 — OWNERSHIP TRANSFER.
//
// The same discipline as the lifecycle above: this file holds the Bulgarian
// sentences and the eligibility the SCREEN needs in order to offer the action at
// all, and decides nothing. Who may transfer, who may receive, and whether the
// outgoing Owner has a licensed and free Търговец seat are answered by the
// database inside one transaction and only rendered here.
// ---------------------------------------------------------------------------

/** Only an Owner may offer a transfer, and only to an ACTIVE Мениджър or Търговец. */
export function canReceiveOwnership(actorRole, member) {
  if (!canManageMember(actorRole, member)) return false;
  return member.is_active === true;
}

/** The consequence, spelled out with the person's name, before anything happens. */
export function ownershipTransferSentence(name) {
  const who = (name || "").trim() || "избрания потребител";
  return (
    `Ще прехвърлите собствеността на организацията към ${who}. ` +
    `${who} ще стане Собственик, а вашата роля ще бъде променена на Търговец.`
  );
}

export const OWNERSHIP_TRANSFER_NOTE =
  "Офертите и клиентите остават без промяна. Никой не губи достъпа си, но след " +
  "прехвърлянето вече няма да можете да управлявате потребители и лиценза.";

/** The seat consequence in one sentence, or an empty string when there is none. */
export function ownershipSeatSentence(seatEffect, targetRole) {
  const sales = seatEffect?.sales;
  if (!sales) return "";
  const before = n(sales.usage);
  const after = n(sales.usage_after);
  const limit = n(sales.limit);
  if (targetRole === "sales" || after === before) {
    return `Заетите места за търговци остават ${before} от ${limit}.`;
  }
  return `Заетите места за търговци стават ${after} от ${limit}, а за мениджъри ${n(
    seatEffect?.manager?.usage_after,
  )} от ${n(seatEffect?.manager?.limit)}.`;
}
